import { createRoot } from "react-dom/client";
import { HelmetProvider } from "react-helmet-async";
import App from "./App.tsx";
import EnvCheckError from "./components/EnvCheckError.tsx";
import "./index.css";
import { installRlsInterceptor } from "./integrations/supabase/rls-interceptor";
import { checkSupabaseEnv } from "./lib/env-check";
import { initSentry, Sentry, sentryEnabled } from "./lib/sentry";

// Initialize error monitoring as early as possible (no-op if VITE_SENTRY_DSN is not set or in preview).
initSentry();

// Validate VITE_SUPABASE_* env vars before mounting the app. If they're
// missing or mismatched, rendering the normal tree would just produce cryptic
// "fetch failed" errors from the Supabase client — surface a clear message
// instead.
const envCheck = checkSupabaseEnv();
if (!envCheck.ok) {
  const errorMessages = envCheck.issues
    .filter((i) => i.severity === "error")
    .map((i) => `[${i.variable}] ${i.message}`);
  if (errorMessages.length > 0) {
    // eslint-disable-next-line no-console
    console.error("Supabase env check failed:\n" + errorMessages.join("\n"));
  }
  const warningMessages = envCheck.issues
    .filter((i) => i.severity === "warning")
    .map((i) => `[${i.variable}] ${i.message}`);
  if (warningMessages.length > 0) {
    // eslint-disable-next-line no-console
    console.warn("Supabase env check warnings:\n" + warningMessages.join("\n"));
  }
}

// Install global PostgREST RLS error logger before any Supabase calls fire.
installRlsInterceptor();


// Guard: unregister service workers in preview/iframe contexts
const isInIframe = (() => {
  try {
    return window.self !== window.top;
  } catch (e) {
    return true;
  }
})();

const isPreviewHost =
  window.location.hostname.includes("id-preview--") ||
  window.location.hostname.includes("lovableproject.com");

if (isPreviewHost || isInIframe) {
  navigator.serviceWorker?.getRegistrations().then((registrations) => {
    registrations.forEach((r) => r.unregister());
  });
}

const AppTree = envCheck.hasErrors ? (
  <EnvCheckError result={envCheck} />
) : (
  <HelmetProvider>
    <App />
  </HelmetProvider>
);

createRoot(document.getElementById("root")!).render(
  sentryEnabled ? (
    <Sentry.ErrorBoundary fallback={<div style={{ padding: 24 }}>Something went wrong. The team has been notified.</div>}>
      {AppTree}
    </Sentry.ErrorBoundary>
  ) : (
    AppTree
  )
);
